import { X } from "lucide-react";
import type {CapaRecord} from "./domain/models";
import {capaSourceLabels} from "./domain/models";
import type {FormKind,FormErrors} from "./capa-form";

export function CapaForm({
  kind,
  selected,
  form,
  errors,
  field,
  saving,
  close,
  submit,
}: {
  kind: Exclude<FormKind, null>;
  selected: CapaRecord | null;
  form: Record<string, string>;
  errors: FormErrors;
  field: (name: string, value: string) => void;
  saving: boolean;
  close: () => void;
  submit: () => void;
}) {
  const titles = {
    create: "DÖF kaydı oluştur",
    evaluate: "DÖF'ü değerlendir",
    complete: "Faaliyeti tamamla",
    verify: "Etkinliği doğrula",
  };
  return (
    <section
      className="form-modal-surface capa-modal"
      role="dialog"
      aria-modal="true"
      aria-label={titles[kind]}
    >
      <div className="section-heading compact">
        <div>
          <span className="section-kicker">
            {kind === "create" ? "Yeni uygunsuzluk" : selected?.capaNo}
          </span>
          <h2>{titles[kind]}</h2>
        </div>
        <button
          className="icon-button"
          aria-label="Formu kapat"
          onClick={close}
        >
          <X size={17} />
        </button>
      </div>
      <div className="capa-form">
        {kind === "create" ? (
          <>
            <Input
              label="Başlık"
              name="title"
              value={form.title ?? ""}
              error={errors.title}
              hint="En az 5 karakter"
              field={field}
            />
            <label className="wide">
              Uygunsuzluk açıklaması
              <textarea
                value={form.description ?? ""}
                aria-invalid={Boolean(errors.description)}
                onChange={(e) => field("description", e.target.value)}
              />
              {errors.description ? (
                <span className="capa-field-error">{errors.description}</span>
              ) : (
                <span className="capa-field-hint">En az 10 karakter</span>
              )}
            </label>
            <label>
              Kaynak
              <select
                value={form.sourceType ?? ""}
                aria-invalid={Boolean(errors.sourceType)}
                onChange={(e) => field("sourceType", e.target.value)}
              >
                <option value="" disabled>
                  Seçin
                </option>
                {Object.entries(capaSourceLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              {errors.sourceType ? (
                <span className="capa-field-error">{errors.sourceType}</span>
              ) : null}
            </label>
            <label>
              Önem derecesi
              <select
                value={form.severity ?? ""}
                aria-invalid={Boolean(errors.severity)}
                onChange={(e) => field("severity", e.target.value)}
              >
                <option value="" disabled>
                  Seçin
                </option>
                <option value="LOW">Düşük</option>
                <option value="MEDIUM">Orta</option>
                <option value="HIGH">Yüksek</option>
                <option value="CRITICAL">Kritik</option>
              </select>
              {errors.severity ? (
                <span className="capa-field-error">{errors.severity}</span>
              ) : null}
            </label>
            <Input
              label="Tedarikçi referansı"
              name="supplierReference"
              value={form.supplierReference ?? ""}
              field={field}
            />
            <Input
              label="Sipariş referansı"
              name="purchaseOrderReference"
              value={form.purchaseOrderReference ?? ""}
              field={field}
            />
            <Input
              label="Malzeme / lot"
              name="materialReference"
              value={form.materialReference ?? ""}
              field={field}
            />
          </>
        ) : null}
        {kind === "evaluate" ? (
          <>
            <Text label="Geçici önlem" name="containmentAction" value={form.containmentAction ?? ""} error={errors.containmentAction} field={field} />
            <Text label="Kök neden" name="rootCause" value={form.rootCause ?? ""} error={errors.rootCause} field={field} />
            <Text label="Faaliyet planı" name="actionPlan" value={form.actionPlan ?? ""} error={errors.actionPlan} field={field} />
            <Input
              label="Faaliyet sorumlusu"
              name="actionOwnerName"
              value={form.actionOwnerName ?? ""}
              error={errors.actionOwnerName}
              field={field}
            />
            <label>
              Termin
              <input
                type="date"
                value={form.dueAt ?? ""}
                aria-invalid={Boolean(errors.dueAt)}
                onChange={(e) => field("dueAt", e.target.value)}
              />
              {errors.dueAt ? (
                <span className="capa-field-error">{errors.dueAt}</span>
              ) : null}
            </label>
          </>
        ) : null}
        {kind === "complete" ? (
          <>
            <Text
              label="Tamamlama açıklaması"
              name="completionNote"
              value={form.completionNote ?? ""}
              error={errors.completionNote}
              field={field}
            />
            <Input
              label="Kanıt referansı"
              name="evidenceReference"
              value={form.evidenceReference ?? ""}
              error={errors.evidenceReference}
              field={field}
            />
          </>
        ) : null}
        {kind === "verify" ? (
          <>
            <label>
              Etkinlik sonucu
              <select
                value={form.effective ?? ""}
                aria-invalid={Boolean(errors.effective)}
                onChange={(e) => field("effective", e.target.value)}
              >
                <option value="" disabled>
                  Seçin
                </option>
                <option value="true">Etkili — kapat</option>
                <option value="false">Yetersiz — faaliyete döndür</option>
              </select>
              {errors.effective ? (
                <span className="capa-field-error">{errors.effective}</span>
              ) : null}
            </label>
            <Text
              label="Değerlendirme açıklaması"
              name="effectivenessNote"
              value={form.effectivenessNote ?? ""}
              error={errors.effectivenessNote}
              field={field}
            />
          </>
        ) : null}
      </div>
      <div className="capa-modal-actions">
        <button className="secondary-button" onClick={close}>
          Vazgeç
        </button>
        <button
          className="primary-button"
          disabled={saving || (kind === "verify" && !form.effective)}
          onClick={submit}
        >
          {saving ? "Kaydediliyor…" : "Kaydet ve ilerlet"}
        </button>
      </div>
    </section>
  );
}
function Input({
  label,
  name,
  value,
  error,
  hint,
  field,
}: {
  label: string;
  name: string;
  value: string;
  error?: string;
  hint?: string;
  field: (n: string, v: string) => void;
}) {
  return (
    <label>
      {label}
      <input
        value={value}
        aria-invalid={Boolean(error)}
        onChange={(e) => field(name, e.target.value)}
      />
      {error ? (
        <span className="capa-field-error">{error}</span>
      ) : hint ? (
        <span className="capa-field-hint">{hint}</span>
      ) : null}
    </label>
  );
}
function Text({
  label,
  name,
  value,
  error,
  field,
}: {
  label: string;
  name: string;
  value: string;
  error?: string;
  field: (n: string, v: string) => void;
}) {
  return (
    <label className="wide">
      {label}
      <textarea
        value={value}
        aria-invalid={Boolean(error)}
        onChange={(e) => field(name, e.target.value)}
      />
      {error ? <span className="capa-field-error">{error}</span> : null}
    </label>
  );
}
